import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { cloneDeepWith, cloneDeep, pick } from 'lodash';

/**
 * set timeout provided a time
 * @param {Number} timeout
 * @returns
 */
const wait = (timeout) => {
  return new Promise((resolve) => setTimeout(resolve, timeout));
};

/**
 * To check whether the object is null or undefined.
 * @param {*} value - To check the object is null or undefined
 * @return {boolean}
 * @private
 */
function isNullOrUndefined(value) {
  return value === undefined || value === null;
}

/**
 * To check whether the object is undefined.
 * @param {*} value - To check the object is undefined
 * @return {boolean}
 * @private
 */
function isUndefined(value) {
  return 'undefined' === typeof value;
}

function deepEqual(a, b) {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date && a.getTime() !== b.getTime()) {
    return false;
  }

  if (a !== Object(a) || b !== Object(b)) {
    return false;
  }

  const props = Object.keys(a);

  if (props.length !== Object.keys(b).length) {
    return false;
  }

  return props.every((p) => deepEqual(a[p], b[p]));
}

/**
 * Copies by non-reference collection and omits key value pairs from object recursively
 * @param collection
 * @param excludeKeys
 * @returns
 */
function omitDeep(collection, excludeKeys) {
  function omitFn(value) {
    if (value && typeof value === 'object') {
      excludeKeys.forEach((key) => {
        delete value[key];
      });
    }
  }

  return cloneDeepWith(collection, omitFn);
}

/**
 * Defines a new read-only property directly on an object and returns the object.
 *
 * @param {object} obj The object on which to define the property.
 * @param {string} prop The name of the property to be defined or modified.
 * @param {any} value The value associated with the property.
 */
function addReadonlyProperty(obj, prop, value) {
  Object.defineProperty(obj, prop, {
    value,
    writable: false,
    enumerable: true
  });
}

/**
 * Defines a new writable property directly on an object and returns the object.
 *
 * @param {object} obj The object on which to define the property.
 * @param {string} prop The name of the property to be defined or modified.
 * @param {any} value The value associated with the property.
 */
function addWritableProperty(obj, prop, value) {
  Object.defineProperty(obj, prop, {
    value,
    writable: true,
    enumerable: true
  });
}

/**
 * To check whether the object is a string.
 * @param {*} value - To check the object is string
 * @return {boolean}
 * @private
 */
function isString(value) {
  return typeof value === 'string';
}

/**
 * Remove null and empty array from object
 * @param {*} obj - object to remove null from
 * @return { Object } object without null or empty array
 */
function removeNullAndEmptyArrays(obj) {
  if (obj === null || obj === undefined || obj.toString().trim() === '') {
    return null;
  }

  if (Array.isArray(obj)) {
    const filteredArray = obj.filter((item) => {
      return item !== null;
    });

    if (filteredArray.length === 0) {
      return null;
    }

    return filteredArray.map((item) => removeNullAndEmptyArrays(item));
  }

  if (typeof obj === 'object') {
    const newObj = {};

    for (const key in obj) {
      const value = removeNullAndEmptyArrays(obj[key]);
      if (value !== null) {
        newObj[key] = value;
      }
    }

    return Object.keys(newObj).length > 0 ? newObj : null;
  }

  return obj;
}

/**
 * get yesterday
 */
const yesterday = () => {
  let d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

/**
 * Formats a number to currency
 * @param { number } numberValue - the value to format
 * @param { string } currency - The ISO currency format in which the number should be formatted
 * @param { string } locale - The locale
 * @returns { string } the formatted number
 */
const formatToNumber = (numberValue, decimalPlaces = 2) => {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimalPlaces
  });

  return formatter.format(numberValue);
};

const formatToCurrency = (numberValue, currency = 'USD', locale = 'en-US') => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  });
  return formatter.format(numberValue);
};

/**
 * Adds percentage to a number
 * @param { number } numberValue - the value to format
 * @returns { string } the number with a % sign
 */
const displayAsPercentage = (value) => {
  return isNaN(value ?? 'N') ? '-' : `${(+value).toFixed(2)}%`;
};

/**
 * formats date to timeago
 * @param { DateTime } date - The date you want to format
 */
const getTimeAgo = (date) => {
  dayjs.extend(relativeTime);
  return dayjs(date).fromNow();
};

/**
 * Normalizes a code by removing all whitespace and converting to uppercase.
 * @param {string} code - The code to normalize
 * @returns {string} The normalized code
 */
function normalizeCode(code) {
  if (!code) return '';
  return code.toString().replace(/\s+/g, '').toUpperCase();
}

function formatDateToWords(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
}

function removeUnderscoreAndCapitalize(str) {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Format a value to 2 decimal places
 * @param { string | number} v
 * @returns number
 */
const to2Dp = (v) => {
  return +Number(v).toFixed(2);
};

export {
  wait,
  formatToNumber,
  isNullOrUndefined,
  isUndefined,
  deepEqual,
  omitDeep,
  addReadonlyProperty,
  addWritableProperty,
  removeNullAndEmptyArrays,

  //lodash methods
  pick,
  cloneDeep,
  yesterday,
  isString,

  //formatters
  to2Dp,
  getTimeAgo,
  formatToCurrency,
  displayAsPercentage,
  normalizeCode,
  formatDateToWords,
  removeUnderscoreAndCapitalize
};
